import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetCourseDetailQuery, useGetCourseStudentsQuery } from '../redux/apiSlice';
import CourseHeader from '../components/CourseHeader';
import CourseHeaderContent from '../components/CourseHeaderContent';
import CourseTabs from '../components/CourseTabs';
import SidebarActions from '../components/SidebarActions';
import CourseInfo from '../components/CourseInfo';

const CourseDetail = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [notification, setNotification] = useState(null);
  const [formattedCourse, setFormattedCourse] = useState(null);
  
  // Pagination states for students
  const [studentsPage, setStudentsPage] = useState(1);
  const [studentsLimit] = useState(10);

  // Validate courseId
  useEffect(() => {
    if (!courseId || courseId === 'undefined' || courseId === 'null' || courseId === '') {
      console.error('❌ Invalid course ID:', courseId);
      setNotification({
        type: 'error',
        message: 'Invalid course ID. Please go back and try again.'
      });
      return;
    }
    
    console.log('🔍 Loading course with ID:', courseId);
  }, [courseId]);

  // Simple API call for course detail
  const { 
    data: apiResponse, 
    isLoading, 
    isError, 
    error,
    refetch 
  } = useGetCourseDetailQuery(courseId, {
    skip: !courseId || courseId === 'undefined' || courseId === 'null' || courseId === ''
  });

  // Get course students with pagination
  const { 
    data: studentsResponse,
    isLoading: studentsLoading,
    isError: studentsError,
    refetch: refetchStudents
  } = useGetCourseStudentsQuery(
    { courseId, page: studentsPage, limit: studentsLimit },
    { skip: activeTab !== 'students' || !courseId }
  );

  // Handle API response
  useEffect(() => {
    if (apiResponse) {
      console.log('📊 Raw API Response for Course Detail:', apiResponse);
      
      if (apiResponse.success) {
        transformCourseData(apiResponse);
        
        setNotification({
          type: 'success',
          message: apiResponse.message || 'Course loaded successfully'
        });
        
        setTimeout(() => setNotification(null), 3000);
      } else {
        setNotification({
          type: 'error',
          message: apiResponse.message || 'Failed to load course'
        });
      }
    }
  }, [apiResponse]);

  // Handle errors
  useEffect(() => {
    if (isError) {
      console.error('❌ API Error:', error);
      setNotification({
        type: 'error',
        message: error?.data?.message || 'Failed to load course details'
      });
    }
  }, [isError, error]);

  // Handle students errors
  useEffect(() => {
    if (studentsError && activeTab === 'students') {
      setNotification({
        type: 'error',
        message: 'Failed to load student data'
      });
    }
  }, [studentsError, activeTab]);

  // Data transformation in component - FIXED FOR CATEGORY AND CURRENCY
  const transformCourseData = (response) => {
    const course = response.course;
    
    if (!course) {
      console.log('❌ No course data in response');
      setFormattedCourse(null);
      return;
    }

    console.log('📊 Transforming course data:', course);
    console.log('📊 Direct category object:', course.category);
    console.log('📊 Direct subcategory object:', course.subcategory);
    console.log('📊 Category name:', course.category?.name);
    console.log('📊 Subcategory name:', course.subcategory?.name);
    
    // Format duration
    const formatDuration = (hours) => {
      if (!hours || hours === 0) return '0h 0m';
      const totalMinutes = Math.round(hours * 60);
      const hoursPart = Math.floor(totalMinutes / 60);
      const minutesPart = totalMinutes % 60;
      return `${hoursPart}h ${minutesPart}m`;
    };

    // Format date
    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    };

    // Calculate total videos
    const totalVideos = course.sections?.reduce((total, section) => 
      total + (section?.videos?.length || 0), 0) || 0;

    console.log('📊 Total videos calculated:', totalVideos);

    // Get category and subcategory - FIXED: Check both direct object and array
    const primaryCategory = course.category?.name || 
                           (course.categories && Array.isArray(course.categories) && course.categories[0]?.name) || 
                           'Uncategorized';
    
    const primarySubcategory = course.subcategory?.name || 
                              (course.subcategories && Array.isArray(course.subcategories) && course.subcategories[0]?.name) || 
                              '';
    
    const primaryLevel = course.level?.[0]?.name || 'All Levels';

    console.log('✅ Final category name:', primaryCategory);
    console.log('✅ Final subcategory name:', primarySubcategory);

    // Extract content arrays
    const objectives = course.objectives?.map(obj => obj?.content || '').filter(Boolean) || [];
    const requirements = course.requirements?.map(req => req?.content || '').filter(Boolean) || [];
    const audiences = course.audiences?.map(aud => aud?.content || '').filter(Boolean) || [];

    // Get instructor name
    const instructorName = course.instructor ? 
      `${course.instructor.fname || ''} ${course.instructor.lname || ''}`.trim() : 
      'Instructor';

    // Total students
    const totalStudents = course.enrollments?.length || course.enrolledStudents?.length || 0;

    // Calculate average rating from feedbacks
    const calculateRating = () => {
      const feedbacks = course.feedbacks || [];
      if (feedbacks.length === 0) return 0;
      
      const totalRating = feedbacks.reduce((sum, feedback) => sum + (feedback.rating || 0), 0);
      return parseFloat((totalRating / feedbacks.length).toFixed(1));
    };

    // Create formatted course object
    const formatted = {
      // Basic info
      id: course.id,
      title: course.title || 'Untitled Course',
      subtitle: course.subtitle || '',
      description: course.description || 'No description available',
      language: course.language || 'English',
      instructorId: course.instructorId,
      thumbnailUrl: course.thumbnailUrl || 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1000&q=80',
      
      // Pricing - Use raw values (already in PKR/Rs)
      price: course.price || 0,
      promoPrice: course.promoPrice || null,
      
      // Status and dates
      status: course.status?.toLowerCase() || 'draft',
      createdAt: course.createdAt,
      updatedAt: course.updatedAt,
      lastUpdated: formatDate(course.updatedAt),
      
      // Categories and level - FIXED
      categories: course.categories || [],
      level: course.level || [],
      category: primaryCategory,
      subcategory: primarySubcategory,
      levelName: primaryLevel,
      
      // Original category and subcategory objects for reference
      categoryData: course.category,
      subcategoryData: course.subcategory,
      
      // Instructor
      instructor: course.instructor,
      instructorName: instructorName,
      instructorImage: course.instructor?.image || '',
      
      // Content - IMPORTANT: Pass sections directly from API
      objectives: objectives,
      requirements: requirements,
      audiences: audiences,
      sections: course.sections || [], // Directly pass sections from API
      videos: course.videos || [],
      
      // Enrollment
      enrollments: course.enrollments || [],
      enrolledStudents: course.enrolledStudents || [],
      totalStudents: totalStudents,
      feedbacks: course.feedbacks || [],
      
      // Calculated fields
      totalDurationHours: course.totalDurationHours || response.totalDurationHours || 0,
      duration: formatDuration(course.totalDurationHours || response.totalDurationHours || 0),
      lectures: totalVideos,
      
      // Step status
      stepStatus: response.stepStatus || 0,
      
      // Ratings and analytics
      rating: calculateRating(),
      revenue: 0,
      enrollmentRate: 0,
      completionRate: 0
    };

    console.log('✅ Formatted course data:', formatted);
    console.log('✅ Category field value:', formatted.category);
    console.log('✅ Subcategory field value:', formatted.subcategory);
    console.log('✅ Price:', formatted.price);
    console.log('✅ Promo Price:', formatted.promoPrice);
    
    setFormattedCourse(formatted);
  };

  // Transform students data with pagination info
  const transformStudentsData = () => {
    if (!studentsResponse || !studentsResponse.success) return {
      students: [],
      pagination: {
        total: 0,
        page: 1,
        limit: studentsLimit,
        totalPages: 0,
        hasNext: false
      }
    };
    
    const students = studentsResponse.students || [];
    
    const formattedStudents = students.map(student => ({
      id: student.id,
      name: student.fullname || `Student ${student.id}`,
      email: student.email || '',
      enrolledDate: student.enrolledAt,
      pricePaid: student.pricePaid || 0
    }));

    return {
      students: formattedStudents,
      pagination: {
        total: studentsResponse.total || 0,
        page: studentsResponse.page || 1,
        limit: studentsResponse.limit || studentsLimit,
        totalPages: studentsResponse.totalPages || 0,
        hasNext: studentsResponse.hasNext || false
      }
    };
  };

  const handleBackToCourses = () => {
    navigate('/courses');
  };

  const handleEditCourse = () => {
    navigate(`/courses/create?edit=${courseId}`);
  };

  const handlePublishCourse = () => {
    console.log('Publishing course:', courseId);
    // Add publish functionality here
  };

  const handleRetry = () => {
    refetch();
    setNotification({
      type: 'info',
      message: 'Refreshing course data...'
    });
    
    setTimeout(() => setNotification(null), 2000);
  };

  const handleStudentsPageChange = (newPage) => {
    setStudentsPage(newPage);
  };

  const closeNotification = () => {
    setNotification(null);
  };

  // Format currency for PKR/Rs - FIXED
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'Rs. 0';
    
    // Format in Pakistani Rupees (Rs.)
    const formattedAmount = new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
    
    // Replace "PKR" with "Rs." for better display
    return formattedAmount.replace('PKR', 'Rs.');
  };

  // Format price without currency symbol (for display only)
  const formatPrice = (amount) => {
    if (!amount && amount !== 0) return '0';
    
    return new Intl.NumberFormat('en-PK', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-green-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Loading course details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if course not found
  if (isError || !formattedCourse) {
    console.log('❌ Error or no formatted course:', { 
      isError, 
      error, 
      formattedCourse,
      courseId,
      apiResponse 
    });
    
    // Check if it's a 404 error
    const is404Error = error?.status === 404 || 
                       error?.data?.statusCode === 404 ||
                       apiResponse?.statusCode === 404;
    
    const errorMessage = is404Error 
      ? `Course with ID "${courseId}" was not found. The course may have been deleted or you don't have access.`
      : (error?.data?.message || apiResponse?.message || 'The course you\'re looking for doesn\'t exist.');
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-green-50 py-8 px-4 sm:px-6 lg:px-8">
        {notification && (
          <div className="max-w-7xl mx-auto mb-6">
            <div className={`rounded-lg p-4 ${
              notification.type === 'error' ? 'bg-red-50 border border-red-200 text-red-800' :
              'bg-blue-50 border border-blue-200 text-blue-800'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {notification.type === 'error' ? '❌' : 'ℹ️'}
                  <span className="ml-2">{notification.message}</span>
                </div>
                <button onClick={closeNotification} className="text-gray-500 hover:text-gray-700">
                  ✕
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Course Not Found</h2>
            <p className="text-gray-600 mb-2">{errorMessage}</p>
            {courseId && courseId !== 'undefined' && (
              <p className="text-gray-500 text-sm mb-6">Course ID: {courseId}</p>
            )}
            <div className="flex justify-center space-x-4">
              <button
                onClick={handleBackToCourses}
                className="px-6 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-all duration-300"
              >
                Back to Courses
              </button>
              <button
                onClick={handleRetry}
                className="px-6 py-3 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 transition-all duration-300"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Get students data with pagination
  const studentsData = activeTab === 'students' ? transformStudentsData() : {
    students: [],
    pagination: {
      total: 0,
      page: 1,
      limit: studentsLimit,
      totalPages: 0,
      hasNext: false
    }
  };

  console.log('📊 Course data for CurriculumTab:', {
    sections: formattedCourse.sections,
    totalSections: formattedCourse.sections?.length,
    videos: formattedCourse.sections?.reduce((total, section) => total + (section?.videos?.length || 0), 0)
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-green-50 py-4 sm:py-6 md:py-8 px-3 sm:px-4 md:px-6 lg:px-8">
      {notification && (
        <div className="max-w-7xl mx-auto mb-6">
          <div className={`rounded-lg p-4 ${
            notification.type === 'error' ? 'bg-red-50 border border-red-200 text-red-800' :
            notification.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' :
            'bg-blue-50 border border-blue-200 text-blue-800'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {notification.type === 'error' ? '❌' : notification.type === 'success' ? '✅' : 'ℹ️'}
                <span className="ml-2">{notification.message}</span>
              </div>
              <button onClick={closeNotification} className="text-gray-500 hover:text-gray-700">
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        <CourseHeader 
          course={formattedCourse}
          onBack={handleBackToCourses}
          onEdit={handleEditCourse}
          onPublish={handlePublishCourse}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          <div className="lg:col-span-2">
            <CourseHeaderContent 
              course={formattedCourse} 
              formatCurrency={formatCurrency}
              formatPrice={formatPrice}
            />
            
            <CourseTabs 
              activeTab={activeTab}
              onTabChange={setActiveTab}
              course={formattedCourse}
              studentsData={{
                ...studentsData,
                isLoading: studentsLoading,
                isError: studentsError,
                onRetry: refetchStudents,
                onPageChange: handleStudentsPageChange,
                currentPage: studentsPage
              }}
              formatCurrency={formatCurrency}
              formatPrice={formatPrice}
            />
          </div>

          <div className="space-y-6">
            <SidebarActions onEdit={handleEditCourse} />
            <CourseInfo 
              course={formattedCourse} 
              formatCurrency={formatCurrency}
              formatPrice={formatPrice}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetail;